import { Color, Spacing } from '@persona-id/design-tokens';
import { EditIcon, TrashIcon } from '@persona-id/design-tokens/icons';
import produce from 'immer';
import { isEqual } from 'lodash/fp';
import { PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import { DragDropContext, Droppable, OnDragEndResponder } from 'react-beautiful-dnd';
import { createPortal } from 'react-dom';
import { batch, useDispatch, useSelector } from 'react-redux';
import { change } from 'redux-form';
import styled, { css } from 'styled-components';

import {
  getUiFormFieldOptionComponentConfigAndPathPairs,
  UiFormFieldOptionComponentConfigPathPair,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateConfigEditor/utils';
import {
  INQUIRY_TEMPLATE_EDITOR_FORM_NAME,
  selector,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/constants';
import { TableSubRow } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/shared';
import { SCREEN_NAV_CONTAINER_PADDING } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenEditor/InquiryTemplateScreenEditor';
import TranslateAllButton from 'dashboard/apps/InquiryTemplateEditor/TranslateAllButton';
import {
  InputsAccordion,
  isEmptyString,
  TranslationInputGroup,
} from 'dashboard/apps/InquiryTemplateEditor/TranslationInputs/TranslationInputs';
import DraggableListItem from 'dashboard/components/DraggableListItem';
import FormControlLabel from 'dashboard/components/FormControls/FormControlLabel';
import MaximizablePanel from 'dashboard/components-lib/MaximizablePanel';
import { FULL_SCREEN_HEADER_HEIGHT } from 'dashboard/layouts/ShowViewLayoutV2';
import TableLayout, { TableTD } from 'dashboard/layouts/TableLayout';
import { reorder } from 'dashboard/lib/dnd/utils';
import useTranslations from 'dashboard/lib/hooks/useTranslations';
import { titleCase } from 'dashboard/lib/utils/stringFormat';
import { InquiryTemplateVersionStep } from 'dashboard/models/inquiry-template-version';
import { RootState } from 'dashboard/redux/store';
import { Z_INDICES } from 'dashboard/styles/variables';
import { INQUIRY_TEMPLATE_EDITOR_SHOW_VIEW_ID } from 'dashboard/views/DashboardInquiryTemplatesShowView/DashboardInquiryTemplatesShowView';
import Button from 'design-system/components/Button';
import Input from 'design-system/components/Input';
import Spacer from 'design-system/components/Spacer';
import { Paragraph } from 'design-system/components/Text';
import Tooltip from 'design-system/components/Tooltip';
import { FontWeight } from 'design-system/variables/text';
import { INQUIRY_LANGUAGES_ENGLISH, InquiryIso6391LanguageCode } from 'lib/constants/locales';
import { FieldSchemaChoices, FieldSchemaMultiChoices, FieldSchemaType } from 'lib/fields';
import { Option, Translation } from 'lib/interfaces/ui-components';
import { captureMessage } from 'lib/reporting';

const form = INQUIRY_TEMPLATE_EDITOR_FORM_NAME;
const CREATE_NEW_OPTION_VALUE = 'new_option';

const PanelContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
`;

const InputContainer = styled.div`
  > * {
    margin-bottom: ${Spacing[1]};
  }
`;

const StyledFormControlLabel = styled(FormControlLabel)`
  font-size: ${({ theme }) => theme.text.paragraph.mdBold.fontSize};
  font-weight: ${FontWeight.Regular};
`;

const ErrorMessage = styled.div<{ narrow?: boolean }>`
  color: ${({ theme }) => theme.color.variants.danger.default};
  font-size: ${({ theme }) => theme.text.paragraph.mdBold.fontSize};
  margin-top: ${({ narrow }) => (narrow ? 0 : `${Spacing[2]}`)};
`;

const CONTAINER_STYLE = css`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 600px;
  min-height: initial;
  z-index: ${Z_INDICES.tooltip};
  padding: ${Spacing[1]};
`;

export type OptionsEditorUsageContext = 'component' | 'field';

type OptionConfigPanelProps = {
  currentOption: OptionWithKey;
  currentLocale: InquiryIso6391LanguageCode;
  options: Option[];
  offsetElementId: string;
  usageContext: OptionsEditorUsageContext;
  handleDeleteOption: () => void;
  handleDismiss: () => void;
  handleSubmitOptionUpdate: (pendingOption: OptionWithKey) => void;
};

const OptionConfigPanel = ({
  currentOption,
  currentLocale,
  options,
  offsetElementId,
  usageContext = 'component',
  handleDeleteOption,
  handleDismiss,
  handleSubmitOptionUpdate,
}: PropsWithChildren<OptionConfigPanelProps>) => {
  const [pendingOption, updatePendingOption] = useState<OptionWithKey>(currentOption);

  useEffect(() => updatePendingOption(currentOption), [currentOption]);

  const enabledLocales = useSelector<RootState, InquiryIso6391LanguageCode[]>(
    (state) => selector(state, 'enabled-locales') ?? [],
  ).filter((locale) => locale !== currentLocale);

  const currentOptionLabel = pendingOption['text-translations']?.find(
    ({ locale }) => locale === currentLocale,
  )?.value;

  const isUsageContextField = usageContext === 'field';

  /**
   * Updates translations for the pending option by first filtering out existing translations
   * with locales included in the new translations, then concatenating the new translations.
   */
  const getOptionWithUpdatedTranslations = (
    option: OptionWithKey,
    newTranslations: Translation[],
  ) =>
    produce(option, (draftOption) => {
      const newTranslationLocales = newTranslations.map(({ locale }) => locale);
      const updatedTranslations = (draftOption?.['text-translations'] ?? [])
        .filter(({ locale }) => !newTranslationLocales.includes(locale))
        .concat(newTranslations);
      draftOption['text-translations'] = updatedTranslations;
    });

  const updatePendingOptionTranslations = useCallback(
    (option: OptionWithKey, newTranslations: Translation[]) =>
      updatePendingOption(getOptionWithUpdatedTranslations(option, newTranslations)),
    [updatePendingOption],
  );

  const { loading, requestTranslations } = useTranslations();

  const handleTranslateAll = useCallback(async () => {
    if (currentOptionLabel != null && !Array.isArray(currentOptionLabel)) {
      const newTranslations = await requestTranslations([
        [currentOptionLabel, enabledLocales.filter((locale) => locale !== currentLocale)],
      ]);
      if (newTranslations[currentOptionLabel]) {
        const newOptionTranslations = Object.entries(newTranslations[currentOptionLabel]).flatMap(
          ([locale, value]) => ({ locale, value }),
        ) as Translation[];
        handleSubmitOptionUpdate(
          getOptionWithUpdatedTranslations(pendingOption, newOptionTranslations),
        );
      }
    }
  }, [
    currentLocale,
    currentOptionLabel,
    enabledLocales,
    pendingOption,
    handleSubmitOptionUpdate,
    requestTranslations,
  ]);

  // validate option label uninqueness
  const isLabelTextDuplicated = options
    .filter(({ key }) => key !== pendingOption.key)
    .some((option: Option) =>
      option['text-translations']?.some((translation) =>
        isEqual(translation, { locale: currentLocale, value: currentOptionLabel }),
      ),
    );

  // validate option value uniqueness
  const isOptionValueDuplicated = options
    ?.filter(({ key }) => key !== pendingOption.key)
    .some((option: Option) => option.value === pendingOption.value);

  const submitOptionUpdate = () => {
    if (!isLabelTextDuplicated && !isOptionValueDuplicated) {
      handleSubmitOptionUpdate(pendingOption);
    }
  };

  // Disables translate all/translate buttons if the source text (option label) is empty.
  const hasMissingSourceText = isEmptyString(currentOptionLabel);

  return createPortal(
    <MaximizablePanel
      title='Edit option'
      offsetElementId={offsetElementId}
      containerStyleOverride={CONTAINER_STYLE}
      fullscreenTop={FULL_SCREEN_HEADER_HEIGHT + SCREEN_NAV_CONTAINER_PADDING}
      rightButton={
        <Button
          width='icon'
          variant='inverse'
          aria-label='Delete option'
          id='option-config-panel__delete-button'
          disabled={options.length <= 2}
          onClick={handleDeleteOption}
        >
          <TrashIcon />
        </Button>
      }
      onOutsideClickAndEscPress={handleDismiss}
    >
      <PanelContentContainer>
        <InputContainer>
          {!isUsageContextField && (
            <>
              <StyledFormControlLabel htmlFor='option-panel__display-name-input'>
                Display Name
              </StyledFormControlLabel>
              <Input
                size='sm'
                placeholder='Enter option display name'
                id='option-panel__display-name-input'
                value={currentOptionLabel}
                onBlur={submitOptionUpdate}
                onChange={(e) =>
                  updatePendingOptionTranslations(pendingOption, [
                    { locale: currentLocale, value: e.target.value },
                  ])
                }
                hasError={isLabelTextDuplicated}
                autoFocus
              />
              <Spacer height={0.5} />
              {isLabelTextDuplicated && (
                <ErrorMessage>Please make sure the option display name is unique</ErrorMessage>
              )}
            </>
          )}
          <StyledFormControlLabel
            htmlFor='option-panel__option-input'
            id='option-panel__option-input__label'
          >
            Option
          </StyledFormControlLabel>
          <Input
            size='sm'
            placeholder='Enter option value'
            id='option-panel__option-input'
            aria-labelledby='option-panel__option-input__label'
            value={pendingOption.value}
            autoFocus={isUsageContextField}
            hasError={isOptionValueDuplicated}
            onBlur={submitOptionUpdate}
            onChange={(e) => {
              updatePendingOption(
                produce(pendingOption, (draft) => {
                  draft.value = e.target.value;
                }),
              );
            }}
          />
          {isOptionValueDuplicated && (
            <ErrorMessage>Please make sure the option value is unique</ErrorMessage>
          )}
          {!isUsageContextField && (
            <InputsAccordion
              caretSize='md'
              title={`Translations (${enabledLocales.length})`}
              menuItemBorder={false}
              menuItemHover={false}
              defaultExpanded
              menuItem
            >
              <>
                <TranslateAllButton
                  id={`${currentOptionLabel}-option`}
                  variant='basic'
                  hasMissingSourceText={hasMissingSourceText}
                  isPending={loading}
                  handleTranslateAll={handleTranslateAll}
                />
                {enabledLocales.map((locale) => {
                  const value = pendingOption['text-translations']?.find(
                    (translation: Translation) => translation.locale === locale,
                  )?.value;
                  return (
                    <TranslationInputGroup
                      key={locale}
                      locale={locale}
                      label={INQUIRY_LANGUAGES_ENGLISH[locale]}
                      inputProps={{
                        size: 'md',
                        onBlur: submitOptionUpdate,
                        onChange: (e) =>
                          updatePendingOptionTranslations(pendingOption, [
                            { locale, value: e.target.value },
                          ]),
                        placeholder: 'No custom translation',
                        value,
                      }}
                      translationSourceText={currentOptionLabel}
                      disabled={hasMissingSourceText}
                      onTranslationsRequest={(translation: string) =>
                        handleSubmitOptionUpdate(
                          getOptionWithUpdatedTranslations(pendingOption, [
                            { locale, value: translation },
                          ]),
                        )
                      }
                    />
                  );
                })}
              </>
            </InputsAccordion>
          )}
        </InputContainer>
      </PanelContentContainer>
    </MaximizablePanel>,
    document.getElementById(INQUIRY_TEMPLATE_EDITOR_SHOW_VIEW_ID) as Element,
  );
};

const StyledTableTR = styled(TableSubRow)`
  width: 100%;
  display: flex;
  justify-content: space-between;
`;

const StyledTableTRHeader = styled(StyledTableTR)`
  background-color: ${Color.CoreSlate50};
  border-radius: ${({ theme }) => theme.borderRadius};
`;

const StyledDisplay = styled(TableTD)`
  display: flex;
  align-items: center;
  width: 50%;
  border-bottom: none;
  padding: 0px ${Spacing[2]};
  overflow-wrap: anywhere;
`;

const StyledHeader = styled(StyledDisplay)`
  width: 50%;
  font-size: ${({ theme }) => theme.text.paragraph.mdBold.fontSize};
  font-weight: ${({ theme }) => theme.text.paragraph.mdBold.fontWeight};
  padding: ${Spacing[2]};
  color: ${Color.CoreSlate1000};

  &:first-child {
    padding-left: ${Spacing[2]};
  }
`;

const StyledDraggableListItem = styled(DraggableListItem)<{ hideDisplayValue?: boolean }>`
  > svg {
    height: ${Spacing[3]};
    fill: black !important;
  }
  padding: ${Spacing[1]} ${Spacing[0]} ${Spacing[1]} ${Spacing[0]};
  gap: 0px;
  ${({ hideDisplayValue }) =>
    hideDisplayValue
      ? css`
          grid-template-columns: 1fr auto;
        `
      : null}
`;

const StyledDraggableListItemText = styled.span`
  font-size: 14px;
`;

const StyledOptionValue = styled(TableTD)<{ disableDrag?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  overflow-wrap: anywhere;
  border-bottom: none;
  padding: ${({ disableDrag }) =>
    disableDrag ? `0px ${Spacing[2]} 0px 28px` : `0px ${Spacing[2]}`};
  line-height: 20px;
`;

const validateOptionsAndDispatch = ({
  updatedOptions,
  currentOptions,
  currentField,
  dispatchCallback,
}: {
  updatedOptions: Option[];
  currentOptions: OptionWithKey[];
  currentField: FieldSchemaChoices | FieldSchemaMultiChoices | undefined;
  dispatchCallback: () => void;
}) => {
  // If there is more than one option with no key when adding an option (should only be one)
  // OR there is an option with no key when removing or updating an option (all options should have keys)
  // we revert the field update and skip the component updates.
  // Not doing so will create an off-by-one error between the field and component options
  const newOptions = updatedOptions.filter(({ key }) => key == null).length;
  const optionAdded = updatedOptions.length > currentOptions.length;
  const optionRemoved = updatedOptions.length < currentOptions.length;

  if (
    currentField != null &&
    ((newOptions > 1 && optionAdded) || (newOptions > 0 && !optionAdded))
  ) {
    captureMessage(
      `Encountered off-by-one update to options for components referencing ${currentField.label}.\n
        Canceling update.`,
      'error',
      {
        fieldKey: currentField.key,
        previousAction: optionAdded
          ? 'Add option'
          : optionRemoved
            ? 'Removed option'
            : 'Update option',
      },
    );
    return;
  } else {
    dispatchCallback();
  }
};

const getOptionsWithKeys = (options: Option[]): OptionWithKey[] =>
  options.map((option) => ({
    ...option,
    key: option.value,
  }));

type FormComponentOptionsEditorProps = {
  options: Option[];
  fieldName: string;
  componentName?: string;
  currentField: FieldSchemaChoices | FieldSchemaMultiChoices | undefined;
  offsetElementId: string;
  currentLocale: InquiryIso6391LanguageCode;
  usageContext?: OptionsEditorUsageContext;
  disabled: boolean;
  handleUpdateComponentConfig?: (componentConfigUpdates: Record<string, any>) => void;
};

type OptionWithKey = Omit<Option, 'key'> & { key: string };

const FormComponentOptionsEditor = ({
  options,
  fieldName,
  componentName,
  currentField,
  offsetElementId,
  currentLocale,
  usageContext = 'component',
  handleUpdateComponentConfig,
  disabled,
}: FormComponentOptionsEditorProps) => {
  // we always first map options to set key to value, as key is used as a stable
  // identifier to correctly update options for all referencing components.
  const [currentOptions, setCurrentOptions] = useState<OptionWithKey[]>(
    getOptionsWithKeys(options),
  );

  useEffect(() => setCurrentOptions(getOptionsWithKeys(options)), [options]);

  const dispatch = useDispatch();

  const [currentOption, setCurrentOption] = useState<OptionWithKey | null>(null);
  const isUsageContextField = usageContext === 'field';

  const steps = useSelector<RootState, InquiryTemplateVersionStep[]>(
    (state) => selector(state, 'steps') ?? [],
  );
  const choicesFieldComponentReferences = useMemo(
    () =>
      currentField != null
        ? getUiFormFieldOptionComponentConfigAndPathPairs(currentField.key, steps)
        : [],
    [currentField, steps],
  );

  const updateOptionsComponentConfig = useCallback(
    (updatedOptions: Option[]) => {
      const newOption = updatedOptions.find(({ key }) => key == null);

      batch(() => {
        // Note (Evan): For each component that references an options field, we need to
        // apply the new values to the options on the component, and not update the component
        // using the options parameter (the text-translations are not set)
        choicesFieldComponentReferences.forEach(
          ({ path, component }: UiFormFieldOptionComponentConfigPathPair) => {
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            const updatedComponentOptions: Option[] = component.config.options
              .map((option) => {
                const currentUpdatedOption = updatedOptions.find(({ key }) => key === option.value);

                if (currentUpdatedOption != null) {
                  // when updating options from config editor, preserve original component option translations.
                  // only update both when editing options in the screen editor context.
                  return produce(option, (draftOption) => {
                    draftOption.value = currentUpdatedOption.value;
                    if (!isUsageContextField && componentName === component.name) {
                      draftOption['text-translations'] = currentUpdatedOption['text-translations'];
                    }
                  });
                } else {
                  return null;
                }
              })
              .filter(Boolean);

            if (newOption != null) {
              /**
               * NOTE (eric): the current behavior will *always* add the new option to *every*
               * component that references the field. We'll want to improve the UX in the future to allow
               * the user to select which components to add the new option to, as well as to add options on the
               * field to the component to dispay independently from adding new options to the field iteslf.
               *
               * generate new translations when adding from config editor,
               * otherwise use the user-inputted translations from the screen editor context.
               */
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              const newTranslations: Translation[] = isUsageContextField
                ? [
                    {
                      locale: 'en-US',
                      value: titleCase(newOption.value),
                    },
                  ]
                : newOption['text-translations'];
              // append new option
              updatedComponentOptions.push({
                value: newOption.value,
                'text-translations': newTranslations,
              });
            }

            // Defer the selected components update to component panel so preview is refreshed
            if (component.name === componentName && handleUpdateComponentConfig) {
              handleUpdateComponentConfig({ options: updatedComponentOptions });
            } else {
              dispatch(change(form, `${path}.config.options`, updatedComponentOptions));
            }
          },
        );
      });
    },
    [
      choicesFieldComponentReferences,
      componentName,
      isUsageContextField,
      handleUpdateComponentConfig,
      dispatch,
    ],
  );

  /**
   * Submits the pending option update to update the parent component and the referenced field.
   * Either replaces the option at index if existing or appends to the options array if new.
   */
  const onOptionSubmit = (pendingOption: OptionWithKey | Option) => {
    const updatedOptions = produce(currentOptions as Option[], (draftOptions) => {
      const optionIndex = draftOptions.findIndex(({ key }) => key === pendingOption.key);
      if (optionIndex !== -1) {
        draftOptions.splice(optionIndex, 1, pendingOption);
      } else {
        draftOptions.push(pendingOption);
      }
    });
    validateOptionsAndDispatch({
      updatedOptions,
      currentOptions,
      currentField,
      dispatchCallback: () => {
        batch(() => {
          updateOptionsComponentConfig(updatedOptions);
          dispatch(
            change(
              form,
              fieldName,
              produce(currentField, (draft: FieldSchemaChoices) => {
                if (draft.config.options != null) {
                  const optionIndex = draft.config.options.findIndex(
                    (option) => option === pendingOption.key,
                  );
                  if (optionIndex !== -1) {
                    draft.config.options[optionIndex] = pendingOption.value;
                  } else {
                    draft.config.options.push(pendingOption.value);
                  }
                }
              }),
            ),
          );
        });
        setCurrentOption({ ...pendingOption, key: pendingOption.value });
      },
    });
  };

  /**
   * Filters out the deleted option by index from both the parent component
   * config as well as the referenced field's string options array.
   */
  const onOptionsDelete = useCallback(() => {
    const updatedOptions = currentOptions.filter(({ key }) => key !== currentOption?.key);

    validateOptionsAndDispatch({
      updatedOptions,
      currentOptions,
      currentField,
      dispatchCallback: () => {
        batch(() => {
          updateOptionsComponentConfig(updatedOptions);
          if (
            currentField != null &&
            [FieldSchemaType.Choices, FieldSchemaType.MultiChoices].includes(currentField.type)
          ) {
            const updatedFieldOptions = (currentField.config.options ?? []).filter(
              (option) => option !== currentOption?.key,
            );
            dispatch(change(form, `${fieldName}.config.options`, updatedFieldOptions));
          }
        });
      },
    });

    setCurrentOption(null);
  }, [
    currentField,
    currentOption?.key,
    currentOptions,
    fieldName,
    dispatch,
    updateOptionsComponentConfig,
  ]);

  /**
   * Reorders the options *only* within the parent component config
   */
  const onOptionsReorder = useCallback<OnDragEndResponder>(
    ({ source, destination }) => {
      if (destination && !isUsageContextField && handleUpdateComponentConfig) {
        const updatedOptions = reorder(currentOptions, source.index, destination.index);
        handleUpdateComponentConfig({ options: updatedOptions });
      }
    },
    [isUsageContextField, currentOptions, handleUpdateComponentConfig],
  );

  const disableAddButton =
    currentOption?.value === CREATE_NEW_OPTION_VALUE ||
    options.some(({ value }) => value === CREATE_NEW_OPTION_VALUE);

  return (
    <>
      {currentOption != null && (
        <OptionConfigPanel
          currentOption={currentOption}
          options={currentOptions}
          currentLocale={currentLocale}
          offsetElementId={offsetElementId}
          usageContext={usageContext}
          handleDeleteOption={onOptionsDelete}
          handleDismiss={() => setCurrentOption(null)}
          handleSubmitOptionUpdate={onOptionSubmit}
        />
      )}
      <TableLayout
        headerTR={
          <StyledTableTRHeader>
            {!isUsageContextField && <StyledHeader>Display name</StyledHeader>}
            <StyledHeader>Option</StyledHeader>
          </StyledTableTRHeader>
        }
        nullState={<></>}
      >
        <DragDropContext onDragEnd={onOptionsReorder}>
          <Droppable droppableId='options-editor__droppable-selected'>
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps}>
                {currentOptions.map((option, index) => {
                  const { 'text-translations': translations, value } = option;
                  return (
                    <StyledDraggableListItem
                      key={value}
                      index={index}
                      draggableId={value}
                      disableDrag={disabled || isUsageContextField}
                      hideDisplayValue={isUsageContextField}
                    >
                      <StyledTableTR>
                        {!isUsageContextField && (
                          <StyledDisplay>
                            <StyledDraggableListItemText>
                              {translations?.find(({ locale }) => locale === currentLocale)?.value}
                            </StyledDraggableListItemText>
                          </StyledDisplay>
                        )}
                        <StyledOptionValue
                          style={{ width: `${isUsageContextField ? '100%' : '50%'}` }}
                        >
                          <StyledDraggableListItemText>{value}</StyledDraggableListItemText>
                        </StyledOptionValue>
                      </StyledTableTR>
                      <Button
                        size='sm'
                        width='icon'
                        variant='inverse'
                        aria-label={`Edit option ${value}`}
                        id='form_component_option_fields_editor__edit_option'
                        disabled={disabled}
                        onClick={() => setCurrentOption(option)}
                      >
                        <EditIcon />
                      </Button>
                    </StyledDraggableListItem>
                  );
                })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </TableLayout>
      <Spacer height={2} />
      <Tooltip
        side='top'
        trigger={
          <Button
            size='sm'
            width='auto'
            variant='basic'
            aria-label='Add option'
            id='form_component_option_fields_editor__create_option'
            disabled={disabled || disableAddButton}
            onClick={() =>
              onOptionSubmit({
                key: undefined,
                value: CREATE_NEW_OPTION_VALUE,
                'text-translations': [{ locale: 'en-US', value: 'New Option' }],
              })
            }
          >
            Add option
          </Button>
        }
        disabled={!disableAddButton}
      >
        <Paragraph style={{ whiteSpace: 'pre-wrap' }}>
          {`Cannot create a new option while an existing option\nis set to the placeholder value (${CREATE_NEW_OPTION_VALUE})`}
        </Paragraph>
      </Tooltip>
    </>
  );
};

export default FormComponentOptionsEditor;
